import { useEffect, useState } from 'react';
import { fetchMemberRecords, type MemberRecords } from '../../../api/work';
import './workload-panels.css';

/**
 * The records behind one member's numbers.
 *
 * **This is the part that made the predecessor's panels worth having.** A
 * bar says "315 commits"; this says which commits, in which repositories,
 * with what messages — so a number you disagree with can be checked rather
 * than argued about. The port had no such endpoint, which is why it became
 * a matrix.
 *
 * **Fetched when opened, never with the page.** Eight record queries per
 * member across eleven members is a second and a half of work for rows
 * nobody has asked to see. The counts arrive with the page; these arrive
 * when a reader clicks a row.
 *
 * **A capped list says so.** The route reports which lists hit its cap and
 * this repeats it per section, because "200 commits" and "at least 200
 * commits" are different claims and only one of them is true here.
 */

/**
 * Which sections to show, in the order a reader descends through them.
 *
 * `optional` means the section is skipped when the endpoint does not send
 * the key at all, rather than shown as "none". The distinction matters for
 * `transitions`: the route omits it when no lane declares stages, and
 * "none" would claim this person made no transitions when the truth is that
 * nothing was configured to count.
 */
const SECTIONS: Array<{ key: string; label: string; optional?: boolean }> = [
  { key: 'open', label: 'Open tickets' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'transitions', label: 'Pipeline transitions', optional: true },
  { key: 'reported', label: 'Reported' },
  { key: 'actions', label: 'Jira actions' },
  { key: 'comments', label: 'Comments' },
  { key: 'commits', label: 'Commits' },
  { key: 'prs_merged', label: 'PRs merged' },
  { key: 'prs_reviewed', label: 'PRs reviewed' },
];

function when(ms: unknown): string {
  if (typeof ms !== 'number' || !ms) return '';
  return new Date(ms).toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
  });
}

/** One row, shaped by what the record actually is rather than by a schema. */
function RecordRow({ row }: { row: Record<string, unknown> }) {
  const key = row.key ?? row.sha ?? row.number;
  const primary =
    typeof row.key === 'string'
      ? row.key
      : typeof row.sha === 'string'
        ? String(row.sha).slice(0, 8)
        : row.number !== undefined
          ? `#${String(row.number)}`
          : '';
  const secondary =
    (typeof row.message === 'string' && row.message) ||
    (typeof row.title === 'string' && row.title) ||
    (typeof row.preview === 'string' && row.preview) ||
    (typeof row.field === 'string'
      ? `${row.field} → ${String(row.to_value ?? '')}`
      : '') ||
    (typeof row.status === 'string' && row.status) ||
    // A status transition: the step it entered is the whole content of the
    // row, and without this the transitions section rendered ticket keys
    // and dates with a blank middle column.
    (typeof row.to_status === 'string' && `→ ${row.to_status}`) ||
    '';
  const at =
    when(row.committed_at_ms) ||
    when(row.merged_at_ms) ||
    when(row.submitted_at_ms) ||
    when(row.changed_at_ms) ||
    when(row.created_at_ms) ||
    when(row.resolved_at_ms);
  const own = row.own === true;
  // `project` fills the same slot as `repo`: which tree this happened in.
  const repo =
    typeof row.repo === 'string'
      ? row.repo
      : typeof row.project === 'string'
        ? row.project
        : '';
  const deep =
    typeof row.open_hours === 'number' && row.open_hours > 24
      ? 'deep'
      : null;

  return (
    <li className="ev__row" key={String(key)}>
      <span className="ev__primary">{primary}</span>
      {repo && <span className="ev__repo">{repo}</span>}
      <span className="ev__secondary">{secondary}</span>
      {deep && (
        <span
          className="ev__tag"
          title="Over 24 hours open to merge — shipping that survives cadence noise"
        >
          {deep}
        </span>
      )}
      {row.own !== undefined && (
        <span className="ev__tag" title={own ? 'their own ticket' : "someone else's ticket"}>
          {own ? 'own' : 'other'}
        </span>
      )}
      <span className="ev__when">{at}</span>
    </li>
  );
}

/**
 * A section whose rows the caller already holds.
 *
 * The planning-failure tickets are in `unplanned_tasks_count`'s breakdown,
 * which the page fetched with `/api/work/workload`; asking the records
 * endpoint for them would be a second implementation of the rule that
 * selected them. `note` says what the list does not show — the metric
 * stores 30 tickets and counts all of them.
 */
export interface ExtraSection {
  label: string;
  rows: Array<Record<string, unknown>>;
  note?: string;
}

interface Props {
  person: string;
  days: number;
  onClose: () => void;
  extra?: ExtraSection[];
}

export function EvidencePopover({ person, days, onClose, extra }: Props) {
  const [data, setData] = useState<MemberRecords | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    fetchMemberRecords(person, days)
      .then((r) => live && setData(r))
      .catch((e: unknown) =>
        live && setError(e instanceof Error ? e.message : String(e)),
      );
    return () => {
      live = false;
    };
  }, [person, days]);

  return (
    <aside className="ev" role="dialog" aria-label={`${person}'s records`}>
      <header className="ev__head">
        <span className="ev__title">
          {person}
          {data?.role && <span className="wll__chip">{data.role}</span>}
          <span className="wlp__dim">last {days}d</span>
        </span>
        <button type="button" className="ev__close" onClick={onClose}>
          Close
        </button>
      </header>

      {/* First, and shown while the records load: the caller already holds
          these rows, so there is nothing to wait for. */}
      {(extra ?? []).map(({ label, rows, note }) => (
        <section className="ev__section" key={`extra-${label}`}>
          <span className="ev__label">
            {label}
            <span className="wlp__dim"> {rows.length}</span>
          </span>
          {rows.length === 0 ? (
            <p className="wlp__dim">none</p>
          ) : (
            <ul className="ev__list">
              {rows.map((row, i) => (
                <RecordRow key={i} row={row} />
              ))}
            </ul>
          )}
          {note && <span className="wlp__dim">{note}</span>}
        </section>
      ))}

      {error ? (
        <p className="wlp__error">{error}</p>
      ) : data === null ? (
        <p className="wlp__dim">Loading…</p>
      ) : (
        SECTIONS.filter(
          ({ key, optional }) => !optional || key in data.records,
        ).map(({ key, label }) => {
          const rows = data.records[key] ?? [];
          const capped = data.truncated.includes(key);
          return (
            <section className="ev__section" key={key}>
              <span className="ev__label">
                {label}
                <span className="wlp__dim">
                  {' '}
                  {capped
                    ? `at least ${data.record_cap}`
                    : rows.length}
                </span>
              </span>
              {rows.length === 0 ? (
                <p className="wlp__dim">none</p>
              ) : (
                <ul className="ev__list">
                  {rows.slice(0, 40).map((row, i) => (
                    <RecordRow key={i} row={row} />
                  ))}
                </ul>
              )}
              {rows.length > 40 && (
                <span className="wlp__dim">
                  showing 40 of {capped ? `${data.record_cap}+` : rows.length}
                </span>
              )}
            </section>
          );
        })
      )}
    </aside>
  );
}
